import type { PublicBranding } from '@/api/types/generated'
import { type Component, type JSX, createEffect } from 'solid-js'

export const BRAND_FONTS: Record<string, { family: string; google?: string }> = {
  default: { family: '' },
  inter: { family: "'Inter', sans-serif", google: 'Inter:wght@400;500;600;700' },
  noto: { family: "'Noto Sans', sans-serif", google: 'Noto+Sans:wght@400;500;600;700' },
  lora: { family: "'Lora', serif", google: 'Lora:wght@400;500;600' },
  jakarta: {
    family: "'Plus Jakarta Sans', sans-serif",
    google: 'Plus+Jakarta+Sans:wght@400;500;600;700',
  },
  'roboto-slab': { family: "'Roboto Slab', serif", google: 'Roboto+Slab:wght@400;500;600' },
}

const SHAPE_RADIUS: Record<string, string> = { pill: '999px', sharp: '6px' }

export function brandCtaClass(b: PublicBranding | null | undefined): string | undefined {
  return b?.palette ? 'brand-cta' : undefined
}

export function brandCtaStyle(b: PublicBranding | null | undefined): JSX.CSSProperties | undefined {
  if (!b?.palette) {
    return undefined
  }
  const radius = SHAPE_RADIUS[b.button_shape ?? '']
  return radius ? { 'border-radius': radius } : {}
}

export function brandThemeVars(
  b: PublicBranding | null | undefined,
  headingFontOnly = false
): Record<string, string> {
  if (!b) {
    return {}
  }
  const vars: Record<string, string> = {}
  const p = b.palette
  if (p) {
    vars['--brand'] = p.brand
    vars['--brand-hover'] = p.brand_hover
    vars['--brand-active'] = p.brand_active
    vars['--brand-soft'] = p.brand_soft
    vars['--on-brand'] = p.on_brand
    vars['--brand-focus-ring'] = p.focus_ring
  }
  const radius = SHAPE_RADIUS[b.button_shape ?? '']
  if (radius) {
    vars['--brand-radius'] = radius
  }
  const font = BRAND_FONTS[b.font ?? 'default']
  if (font?.family) {
    vars['--brand-font'] = font.family
    vars['--font-display'] = font.family
    if (!headingFontOnly) {
      vars['--font-sans'] = font.family
    }
  }
  return vars
}

export function ensureGoogleFont(fontKey: string) {
  const font = BRAND_FONTS[fontKey]
  if (!font?.google) {
    return
  }
  const id = `brand-font-${fontKey}`
  if (document.getElementById(id)) {
    return
  }
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`
  document.head.appendChild(link)
}

export const BrandTheme: Component<{
  branding: PublicBranding | null | undefined
  headingFontOnly?: boolean
  children: JSX.Element
}> = (props) => {
  createEffect(() => {
    const key = props.branding?.font
    if (key) {
      ensureGoogleFont(key)
    }
  })
  return (
    <div
      class="contents"
      style={{
        ...brandThemeVars(props.branding, props.headingFontOnly),
        ...(!props.headingFontOnly &&
        props.branding?.font &&
        BRAND_FONTS[props.branding.font]?.family
          ? { 'font-family': 'var(--brand-font)' }
          : {}),
      }}
    >
      {props.children}
    </div>
  )
}
